import { Input } from '@components'
import { Delete } from '@icons'
import cn from '@integrations/classnames'
import type { FocusEvent } from '@integrations/react'
import { tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import QuestionnaireYesNoField from '../QuestionnaireYesNoField'

interface Props {
  formValues: UseFormValuesOutput
  handleSave: (event?: FocusEvent<HTMLElement> | string | any) => void
  businessWebsite: string[]
  businessWebSiteError: string[]
  businessWebsiteChange: (index: number, value: string) => void
  businessWebsiteAdd: () => void
  businessWebsiteRemove: () => void
  countryOptions: {
    value: string
    label: string
  }[]
}

function QuestionnaireSecondStep(props: Props) {
  const {
    formValues,
    handleSave,
    businessWebsite,
    businessWebSiteError,
    businessWebsiteChange,
    businessWebsiteAdd,
    businessWebsiteRemove,
    countryOptions
  } = props

  const handleLicensedClick = (value: string) => {
    const parsedValue = Boolean(value)

    formValues.setValue('isLicensed', parsedValue)
    formValues.setRequired('typeOfLicense', parsedValue)
    // formValues.setRequired('nameOfRegulator', parsedValue)
    formValues.setRequired('issuingCountry', parsedValue)
    formValues.setRequired('licenseNumber', parsedValue)
  }

  return (
    <>
      <div className={cn('subtitle')}>
        {tr(
          'FRONTEND.QUESTIONNAIRE.KYB.PROVIDE_ADDITIONAL_INFORMATION',
          'Please provide additional business information and documents for us to be able'
        )}
      </div>

      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.BUSINESS_OWNER_EMAIL', "Business owner's email address")}
        {...formValues.registerInput('emailAddressOfBusinessOwner')}
        data-test='business-owner-email-address'
        onBlur={handleSave}
      />

      <Input
        type='number'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.UNIQUES_TAX_REFERENCE', 'Uniques tax reference/Tax ID')}
        {...formValues.registerInput('taxId')}
        data-test='uniques-tax-reference-or-tax-id'
        onBlur={handleSave}
      />

      {businessWebsite.map((element, index) => (
        <Input
          key={`website-${index}`}
          type='text'
          value={element}
          error={businessWebSiteError[index]}
          required={true}
          parentClassName='website'
          name={`business-website-${index}`}
          data-test={`business-website-${index}`}
          label={index === 0 ? tr('FRONTEND.QUESTIONNAIRE.KYB.BUSINESS_WEBSITE', 'Official website URL') : null}
          placeholder={tr('FRONTEND.QUESTIONNAIRE.KYB.BUSINESS_WEBSITE', 'Official website URL')}
          onChange={(_: string, value: string) => businessWebsiteChange(index, value)}
          hideErrorLine
          onBlur={handleSave}
        />
      ))}
      <div className={cn('add-and-remove-wrapper')}>
        <div className={cn('add')} onClick={() => businessWebsiteAdd()} data-test='add'>
          {tr('FRONTEND.QUESTIONNAIRE.BUTTON_ADD', 'Add')}
        </div>
        {businessWebsite.length > 1 ? (
          <div className={cn('remove')} onClick={() => businessWebsiteRemove()} data-test='remove'>
            <div className={cn('remove-icon')}>
              <Delete />
            </div>
            <span>{tr('FRONTEND.QUESTIONNAIRE.BUTTON_REMOVE', 'Remove')}</span>
          </div>
        ) : null}
      </div>

      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.INDUSTRY_LABEL', 'Industry')}
        {...formValues.registerInput('industry')}
        data-test='industry'
        onBlur={handleSave}
      />

      <QuestionnaireYesNoField
        heading={tr('FRONTEND.QUESTIONNAIRE.KYB.ENTITY_LICENSED_LABEL', 'Is the entity licensed?')}
        selected={formValues.getValue('isLicensed')}
        setSelected={handleLicensedClick}
        yesDataTest='licensed-yes'
        noDataTest='licensed-no'
      />

      {formValues.getValue('isLicensed') && (
        <>
          <Input
            type='text'
            label={tr('FRONTEND.QUESTIONNAIRE.KYB.TYPE_OF_LICENSE', 'Type of license')}
            {...formValues.registerInput('typeOfLicense')}
            data-test='type-of-license'
            onBlur={handleSave}
          />
          <Input
            type='text'
            label={tr('FRONTEND.QUESTIONNAIRE.KYB.NAME_OF_THE_REGULATOR', 'Name of the regulator')}
            {...formValues.registerInput('nameOfRegulator')}
            data-test='name-of-regulator'
            onBlur={handleSave}
          />
          <Input
            type='text'
            label={tr('FRONTEND.QUESTIONNAIRE.KYB.ISSUING_COUNTRY', 'Issuing country')}
            {...formValues.registerInput('issuingCountry')}
            data-test='issuing-country'
            onBlur={handleSave}
          />
          <Input
            type='number'
            label={tr('FRONTEND.QUESTIONNAIRE.KYB.LICENSE_NUMBER', 'License number')}
            {...formValues.registerInput('licenseNumber')}
            data-test='license-number'
            onBlur={handleSave}
          />
        </>
      )}

      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.BUSINESS_ACTIVITY', 'Business activity')}
        {...formValues.registerInput('businessActivity')}
        data-test='business-activity'
        onBlur={handleSave}
      />

      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.COUNTRY_OF_INCORPORATION', 'Country of incorporation')}
        {...formValues.registerInput('countryOfIncorporation')}
        data-test='country-of-incorporation'
        onBlur={handleSave}
      />

      <Input
        type='select'
        isSearchable
        isMultiSelect
        options={countryOptions}
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.COUNTRIES_OF_OPERATION', 'Countries of operation')}
        {...formValues.registerInput('operationCountries')}
        data-test='operation-countries'
        onBlur={() => handleSave('operationCountries')}
      />

      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.SOLUTION_REQUIRED', 'Solution required')}
        {...formValues.registerInput('solutionRequired')}
        data-test='solution-required'
        onBlur={handleSave}
      />
    </>
  )
}

export default QuestionnaireSecondStep
